/** @jsx jsx */
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, Button, Flex, jsx, Spinner, Text } from 'theme-ui'
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel'
import { useCart, useTranslate } from '@arfabrands/gatsby-theme-assembly'

const PromoCode = ({ promotion }) => {
  const translate = useTranslate()
  const { cart, addPromoCode, removePromoCode } = useCart()

  const { register, handleSubmit, errors } = useForm({
    defaultValues: {
      promoCode: promotion ? promotion.code : ''
    }
  })

  const { isFetching } = cart.promoCode
  const [apiError, setApiError] = useState(null)
  const [isFormActive, setIsFormActive] = useState(promotion ? true : false)

  const onSubmit = async (data, e) => {
    if (promotion) {
      try {
        setApiError(null)
        await removePromoCode(promotion.code)
        e.target.reset()
      } catch (error) {
        setApiError(error)
      }
    } else {
      try {
        setApiError(null)
        await addPromoCode(data.promoCode)
      } catch (error) {
        setApiError(error)
      }
    }
  }

  const activatePromoCodeForm = event => {
    if (event) event.preventDefault()
    setIsFormActive(true)
  }

  if (!isFormActive) {
    return (
      <Flex sx={{ flexDirection: 'column', my: '1rem' }}>
        <Box as="form" onSubmit={e => activatePromoCodeForm(e)}>
          <Button sx={{
            color: 'rgb(81, 39, 115)',
            backgroundColor: 'rgb(197, 179, 215)',
            '&:hover': {
              backgroundColor: 'rgb(197, 179, 215)',
              filter: 'brightness(0.9)',
            },
            borderRadius: '0.25rem',
            fontFamily: 'DIN Alternate Bold,Helvetica,Arial,Sans-Serif',
            padding: '12px 16px',
            transition: 'filter 0.15s ease-in-out 0s',
            width: '100%',
          }}>
            <Text variant="link">{translate('cart.apply_promo_code')}</Text>
          </Button>
        </Box>
      </Flex>
    )
  }

  return (
    <Flex sx={{ flexDirection: 'column', my: '1rem' }}>
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Flex
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Box
            sx={{
              width: '80%',
              marginRight: '1rem'
            }}
          >
            <FormInputWithLabel
              label={translate('cart.promo_code')}
              title={translate('cart.promo_code')}
              aria-label={translate('cart.promo_code')}
              placeholder={translate('cart.promo_code')}
              name="promoCode"
              disabled={promotion}
              ref={register({ required: true })}
            />
          </Box>
          <Button sx={{
            color: 'rgb(81, 39, 115)',
            backgroundColor: 'rgb(197, 179, 215)',
            '&:hover': {
              backgroundColor: 'rgb(197, 179, 215)',
              filter: 'brightness(0.9)',
            },
            borderRadius: '0.25rem',
            fontFamily: 'DIN Alternate Bold,Helvetica,Arial,Sans-Serif',
            transition: 'filter 0.15s ease-in-out 0s',
          }}>
            {isFetching && <Spinner size="15" color="inherit" />}
            {!isFetching && (
              <Text>
                {promotion
                  ? translate('cart.remove_promo_code_button')
                  : translate('cart.apply_promo_code_button')}
              </Text>
            )}
          </Button>
        </Flex>
      </Box>
      {apiError && (
        <Text
          variant="formError"
          sx={{
            color: 'errorDark',
            backgroundColor: 'errorLight',
            px: '16px',
            py: '8px'
          }}
        >
          {apiError.message}
        </Text>
      )}
      {errors.promoCode && (
        <Text
          variant="formError"
          sx={{
            color: 'errorDark',
            backgroundColor: 'errorLight',
            px: '16px',
            py: '8px'
          }}
        >
          {translate('validation.required_field')}
        </Text>
      )}
    </Flex>
  )
}

PromoCode.propTypes = {
  promotion: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    amount: PropTypes.number,
    displayAmount: PropTypes.string
  })
}

export default PromoCode
